import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';

// Import components that can be rendered in PDF
import ReportFeedbackBox from '../boxes/ReportFeedbackBox.js';
import ReportViewBox from '../boxes/ReportViewBox.js';
import MedicReportFeedbackBox from '../boxes/MedicReportFeedbackBox.js';
import ProtocolAssessmentBox from '../boxes/ProtocolAssessmentBox.js';
import ReportTestsBox from '../boxes/ReportTestsBox.js';
import ReportPdfBox from '../boxes/ReportPdfBox.js';
import './PDFPage.css';

/**
 * PDFPage - A specialized page for aggregating and formatting components for PDF generation
 * 
 * Query Parameters:
 * - type: Type of content to render (e.g., 'report', 'custom')
 * - reportId: Report ID (if rendering report content)
 * - tab: Tab index to render (0-based)
 * - token: Authentication token (optional, for server-side PDF generation)
 * - uid: User ID (optional, for server-side PDF generation)
 * 
 * Example URLs:
 * - /pdf?type=report&reportId=123&tab=0
 * - /pdf?type=report&reportId=123&tab=1&token=xyz&uid=5
 */
const PDFPage = () => {
    const [searchParams] = useSearchParams();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // Extract query parameters
    const type = searchParams.get('type') || 'report';
    const reportId = searchParams.get('reportId');
    const tabIndex = parseInt(searchParams.get('tab') || '0', 10);
    const token = searchParams.get('token');
    const uid = searchParams.get('uid');
    const markdownContent = searchParams.get('markdown'); // Base64 encoded markdown content

    // Mock user object for PDF generation (if uid provided)
    useEffect(() => {
        if (uid) {
            // Create a minimal user object for PDF generation
            // In production, you might want to validate the token and fetch user data
            // Include ADMIN_SUB subscription to allow Protocol Assessment rendering
            setUser({
                uid: parseInt(uid, 10),
                subscriptions: ['CADMUS_QA', 'CADMUS_QI', 'ADMIN_SUB'] // Default subscriptions for PDF (includes ADMIN_SUB for Protocol Assessment)
            });
        }
        setLoading(false);
    }, [uid, token]);

    // Component mapping for report tabs
    const reportComponents = [
        {
            label: "Report Overview",
            component: ReportFeedbackBox,
            props: { user: user, reportId: reportId, isSmallScreen: false }
        },
        {
            label: "Medic View",
            component: MedicReportFeedbackBox,
            props: { user: user, reportId: reportId, isSmallScreen: false }
        },
        {
            label: "Detailed PCR",
            component: ReportViewBox,
            props: { user: user, reportId: reportId, isSmallScreen: false }
        },
        {
            label: "All Tests",
            component: ReportTestsBox,
            props: { user: user, reportId: reportId, isSmallScreen: false }
        },
        {
            label: "Protocol Assessment",
            component: ProtocolAssessmentBox,
            props: { user: user, reportId: reportId, isSmallScreen: false }
        },
        {
            label: "Report PDF",
            component: ReportPdfBox,
            props: { user: user, reportId: reportId, isSmallScreen: false }
        }
    ];

    // Render content based on type
    const renderContent = () => {
        if (loading) {
            return (
                <div className="pdf-loading">
                    <div>Loading content for PDF generation...</div>
                </div>
            );
        }

        switch (type) {
            case 'report':
                const showCover = searchParams.get('cover') === 'true';
                const includeProtocol = searchParams.get('includeProtocol') === 'true';
                const showMarkdown = searchParams.get('markdownPage') === 'true';
                
                // Show markdown page if requested (no authentication required)
                if (showMarkdown && markdownContent) {
                    try {
                        const decodedMarkdown = decodeURIComponent(atob(markdownContent));
                        return (
                            <div className="pdf-markdown-page">
                                <div className="pdf-markdown-content">
                                    <ReactMarkdown>{decodedMarkdown}</ReactMarkdown>
                                </div>
                            </div>
                        );
                    } catch (error) {
                        return (
                            <div className="pdf-error">
                                <h2>Error Rendering Markdown</h2>
                                <p>Could not decode markdown content.</p>
                            </div>
                        );
                    }
                }
                
                // Show cover page if requested (no authentication required)
                if (showCover) {
                    return (
                        <div className="pdf-cover-page">
                            <h1 className="pdf-cover-title">CADMUS Report</h1>
                            <div className="pdf-cover-content">
                                <p>
                                    Version 1.0.0
                                </p>
                                {/* <p>
                                    All data, findings, and recommendations presented in this report are based on standardized
                                    protocols and evidence-based practices in emergency medical services.
                                </p> */}
                                {/* {reportId && (
                                    <div className="pdf-cover-meta">
                                        <p><strong>Report ID:</strong> {reportId}</p>
                                    </div>
                                )} */}
                            </div>
                        </div>
                    );
                }
                
                // For report content, require reportId and user
                if (!reportId) {
                    return (
                        <div className="pdf-error">
                            <h2>Missing Report ID</h2>
                            <p>Please provide a reportId parameter in the URL.</p>
                        </div>
                    );
                }

                if (!user) {
                    return (
                        <div className="pdf-error">
                            <h2>Authentication Required</h2>
                            <p>Please provide uid and token parameters for authenticated content.</p>
                        </div>
                    );
                }

                // Render the selected tab component
                const selectedComponent = reportComponents[tabIndex];
                if (!selectedComponent) {
                    return (
                        <div className="pdf-error">
                            <h2>Invalid Tab Index</h2>
                            <p>Tab index {tabIndex} is out of range. Available tabs: 0-{reportComponents.length - 1}</p>
                        </div>
                    );
                }
                
                // If includeProtocol is true and we're on tab 0 (Report Overview), render both
                if (includeProtocol && tabIndex === 0) {
                    const ReportComponent = reportComponents[5].component;

                    return (
                        <div className="pdf-content-container">
                            <div className="pdf-content">
                                <ReportComponent {...reportComponents[5].props} />
                            </div>
                        </div>
                    );
                }
                
                // Regular content without the "Report Overview" header
                const ComponentToRender = selectedComponent.component;
                return (
                    <div className="pdf-content-container">
                        <div className="pdf-content">
                            <ComponentToRender {...selectedComponent.props} />
                        </div>
                    </div>
                );

            case 'custom':
                // Placeholder for custom content types
                return (
                    <div className="pdf-content-container">
                        <h1>Custom PDF Content</h1>
                        <p>Custom content rendering not yet implemented.</p>
                    </div>
                );

            default:
                return (
                    <div className="pdf-error">
                        <h2>Invalid Content Type</h2>
                        <p>Unknown type: {type}. Supported types: 'report', 'custom'</p>
                    </div>
                );
        }
    };

    return (
        <div className="pdf-page">
            <div className="pdf-wrapper">
                {renderContent()}
            </div>
        </div>
    );
};

export default PDFPage;

